import { motion } from "framer-motion";
import { renderAA } from "../aa";
import { POINTS, Counter, EASE } from "./shared";

const Header = ({ sub }) => (
  <div className="mb-8">
    <div className="flex items-center gap-3 mb-5">
      <span className="h-px w-10 bg-[#EDA13A]" />
      <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#EDA13A]">03 · The Proof</span>
    </div>
    <h2 className="font-display font-medium tracking-tight text-3xl lg:text-4xl text-[#F7F5F0] leading-[1.1] max-w-3xl">
      Proof is in the Value: {renderAA("Agentic Alpha")} in Numbers
    </h2>
    {sub && <p className="text-sm text-[#F7F5F0]/55 mt-3 max-w-2xl leading-relaxed">{sub}</p>}
  </div>
);

const StatText = ({ p, size = "text-3xl lg:text-4xl" }) => (
  <span className={`font-display font-semibold text-[#EDA13A] whitespace-nowrap ${size}`}>
    {p.pre}
    <Counter value={p.value} />
    {p.suffix}
  </span>
);

/* ------------------------------------------------------------------ */
/* Option A — Head-to-Head Dual Panels                                 */
/* ------------------------------------------------------------------ */
export const ProofDualPanels = () => (
  <div className="h-full w-full bg-[#211307] p-6 lg:p-10 overflow-auto">
    <Header sub="Two worlds, side by side — conventional enterprise AI versus what Agentic Alpha delivers, metric for metric." />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
      {/* Panel headers */}
      <div className="bg-[#241608] px-6 py-4">
        <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#F7F5F0]/40">Conventional Enterprise AI</span>
      </div>
      <div className="bg-[#2E1B0E] px-6 py-4 border-l border-[#EDA13A]/25">
        <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#EDA13A] font-semibold">With Intugle</span>
      </div>
      {/* Rows */}
      {POINTS.map((p, i) => (
        <div key={p.id} className="contents">
          <div className="bg-[#241608] px-6 py-5">
            <p className="font-semibold text-[#F7F5F0] text-sm">{p.metric}</p>
            <p className="text-sm text-[#F7F5F0]/40 mt-1 leading-snug">{p.conv}</p>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
            className="bg-[#2E1B0E] px-6 py-5 border-l border-[#EDA13A]/25 flex flex-col justify-center"
          >
            <StatText p={p} size="text-2xl lg:text-3xl" />
            <p className="text-[13px] text-[#F7F5F0]/70 mt-1 leading-snug">{p.tail}</p>
          </motion.div>
        </div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Option C (Light) — Versus Scoreboard on cream theme                 */
/* ------------------------------------------------------------------ */
export const ProofScoreboardLight = () => (
  <div className="h-full w-full bg-[#F7F5F0] p-6 lg:p-10 overflow-auto">
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-5">
        <span className="h-px w-10 bg-[#8A5730]" />
        <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8A5730]">03 · The Proof</span>
      </div>
      <h2 className="font-display font-medium tracking-tight text-3xl lg:text-4xl text-[#3E2818] leading-[1.1] max-w-3xl">
        Proof is in the Value: {renderAA("Agentic Alpha")} in Numbers
      </h2>
      <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">
        A head-to-head scoreboard — conventional enterprise AI versus Agentic Alpha, metric for metric.
      </p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5">
        <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#3E2818]/55">
          <span className="w-2.5 h-2.5 rounded-full bg-[#3E2818]/25" /> Conventional Enterprise AI
        </span>
        <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A5730]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#8A5730]" /> With Intugle
        </span>
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
      {POINTS.map((p, i) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.55, delay: i * 0.05, ease: EASE }}
          className="rounded-2xl border border-[#3E2818]/10 bg-white px-6 lg:px-8 py-6 transition-all duration-300 hover:border-[#8A5730]/35 hover:shadow-[0_20px_50px_-30px_rgba(62,40,24,0.35)]"
          data-testid={`score-light-row-${p.id}`}
        >
          <p className="text-center font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A5730] mb-4">{p.metric}</p>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-6">
            <p className="text-right text-sm text-[#3E2818]/50 leading-snug">{p.conv}</p>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#3E2818]/45 border border-[#3E2818]/15 rounded-full px-2.5 py-1">VS</span>
            <div className="text-left">
              <span className="font-display font-semibold text-[#8A5730] text-2xl lg:text-3xl whitespace-nowrap">
                {p.pre}
                <Counter value={p.value} />
                {p.suffix}
              </span>
              <p className="text-[12px] text-[#3E2818]/60 mt-0.5 leading-snug">{p.tail}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Option B — Data-Viz Bars                                            */
/* ------------------------------------------------------------------ */
const BAR = { ttv: 60, precision: 95, productivity: 90, tco: 50, impact: 40, scale: 80 };
export const ProofBars = () => (
  <div className="h-full w-full bg-[#211307] p-6 lg:p-10 overflow-auto">
    <Header sub="The gap made visual — muted conventional baselines against the gold Agentic Alpha result for every metric." />
    <div className="space-y-6">
      {POINTS.map((p, i) => (
        <div key={p.id} className="grid md:grid-cols-[0.8fr_2fr] gap-4 md:gap-8 items-center py-4 border-t border-white/10" data-testid={`bars-row-${p.id}`}>
          <div className="flex items-center gap-3">
            <span className="font-mono2 text-xs text-[#9CA888]">{String(i + 1).padStart(2, "0")}</span>
            <p className="font-semibold text-[#F7F5F0] text-sm lg:text-base">{p.metric}</p>
          </div>
          <div className="space-y-2.5">
            {/* conventional bar */}
            <div className="flex items-center gap-3">
              <div className="h-2.5 rounded-full bg-white/[0.06] flex-1 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "26%" }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 1, ease: EASE }}
                  className="h-full rounded-full bg-white/20"
                />
              </div>
              <span className="w-40 shrink-0 text-[11px] text-[#F7F5F0]/40 leading-snug hidden lg:block">{p.conv}</span>
            </div>
            {/* intugle bar */}
            <div className="flex items-center gap-3">
              <div className="h-2.5 rounded-full bg-white/[0.06] flex-1 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${BAR[p.id]}%` }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 1.3, ease: EASE }}
                  className="h-full rounded-full bg-gradient-to-r from-[#8A5730] to-[#EDA13A]"
                />
              </div>
              <span className="w-40 shrink-0 flex items-baseline gap-1.5">
                <StatText p={p} size="text-xl lg:text-2xl" />
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Option C — Versus Scoreboard (2 x 3 matrix, toned to theme)         */
/* ------------------------------------------------------------------ */
export const ProofScoreboard = () => (
  <div className="h-full w-full bg-[#211307] p-6 lg:p-10 overflow-auto">
    <Header sub="A head-to-head scoreboard — conventional enterprise AI versus Agentic Alpha, metric for metric." />
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 -mt-3 mb-6">
      <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F7F5F0]/50">
        <span className="w-2.5 h-2.5 rounded-full bg-white/25" /> Conventional Enterprise AI
      </span>
      <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#E8AA68]">
        <span className="w-2.5 h-2.5 rounded-full bg-[#E8AA68]" /> With Intugle
      </span>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
      {POINTS.map((p, i) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.55, delay: i * 0.05, ease: EASE }}
          className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 lg:px-8 py-6 transition-colors duration-300 hover:border-white/20 hover:bg-white/[0.05]"
          data-testid={`score-row-${p.id}`}
        >
          <p className="text-center font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#9CA888] mb-4">{p.metric}</p>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-6">
            <p className="text-right text-sm text-[#F7F5F0]/45 leading-snug">{p.conv}</p>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#F7F5F0]/45 border border-white/15 rounded-full px-2.5 py-1">VS</span>
            <div className="text-left">
              <span className="font-display font-semibold text-[#E8AA68] text-2xl lg:text-3xl whitespace-nowrap">
                {p.pre}
                <Counter value={p.value} />
                {p.suffix}
              </span>
              <p className="text-[12px] text-[#F7F5F0]/70 mt-0.5 leading-snug">{p.tail}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Option D — Enhanced Current Table (gold lane, light touch)          */
/* ------------------------------------------------------------------ */
const COLS = "md:grid-cols-[0.9fr_1.1fr_1.3fr]";
export const ProofTablePlus = () => (
  <div className="h-full w-full bg-[#211307] p-6 lg:p-10 overflow-auto">
    <Header sub="The familiar comparison table — with the Agentic Alpha column elevated as a highlighted gold lane." />
    <div className="hidden md:block">
      <div className={`grid ${COLS}`}>
        <div className="px-4 py-3"><span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#F7F5F0]/40">Enterprise Metric</span></div>
        <div className="px-4 py-3"><span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#F7F5F0]/40">Conventional Enterprise AI</span></div>
        <div className="px-6 py-3 rounded-t-2xl bg-[#EDA13A]/[0.06] border-x border-t border-[#EDA13A]/25"><span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#EDA13A] font-semibold">With Intugle</span></div>
      </div>
      {POINTS.map((p, i) => {
        const last = i === POINTS.length - 1;
        return (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.55, delay: i * 0.05, ease: EASE }}
            className={`group grid ${COLS} items-stretch`}
            data-testid={`tableplus-row-${p.id}`}
          >
            <div className="flex items-center gap-3 px-4 py-6 border-t border-white/10">
              <span className="font-mono2 text-xs text-[#9CA888]">{String(i + 1).padStart(2, "0")}</span>
              <p className="font-semibold text-[#F7F5F0] text-base leading-tight">{p.metric}</p>
            </div>
            <div className="flex items-center px-4 py-6 border-t border-white/10">
              <p className="text-sm text-[#F7F5F0]/45 leading-relaxed">{p.conv}</p>
            </div>
            <div className={`flex flex-col justify-center px-6 py-6 bg-[#EDA13A]/[0.06] border-x border-t border-[#EDA13A]/25 transition-colors duration-300 group-hover:bg-[#EDA13A]/[0.1] ${last ? "rounded-b-2xl border-b" : ""}`}>
              <StatText p={p} />
              <p className="text-[13px] text-[#F7F5F0]/70 mt-1 leading-snug">{p.tail}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
    <p className="md:hidden text-sm text-[#F7F5F0]/50">Preview this option on a wider screen to see the full comparison table.</p>
  </div>
);
